import { useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt, EnvelopeSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we'll write back soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email: newsletterEmail });
      toast.success(data.already_subscribed ? "You're already with us. Thank you!" : "Welcome to the circle.");
      setNewsletterEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe. Try again.");
    } finally {
      setSubLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F4EFE6]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <p className="label-eyebrow mb-5">Stay close</p>
          <h2 className="font-headline font-light text-4xl md:text-5xl text-[#1F1A17] leading-tight">
            Join the <span className="italic text-[#C25934]">community circle</span>.
          </h2>
          <p className="mt-6 text-lg text-[#5A524A] leading-relaxed">
            A quiet monthly note: stories from the evenings, what's needed in the pantry, and
            how to help. No noise. No selling. Just news from the hearth.
          </p>

          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3">
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="you@example.com"
              className="hearth-input flex-1"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={subLoading}
              className="btn-hearth rounded-full bg-[#1F1A17] hover:bg-[#3a3128] text-white px-6 py-3 font-semibold flex items-center justify-center gap-2"
            >
              <EnvelopeSimple size={18} weight="duotone" />
              {subLoading ? "Sending…" : "Subscribe"}
            </button>
          </form>
        </div>

        <form
          onSubmit={submitContact}
          className="md:col-span-7 bg-[#FDFBF7] border border-[#EADAB9] rounded-3xl p-8 md:p-10"
          data-testid="contact-form"
        >
          <p className="label-eyebrow mb-5">Write to us</p>
          <h3 className="font-headline text-2xl md:text-3xl text-[#1F1A17] mb-6">
            Questions, partnerships, ideas — we're listening.
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              data-testid="contact-name"
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              className="hearth-input"
              required
            />
            <input
              data-testid="contact-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="your@email.com"
              className="hearth-input"
              required
            />
          </div>
          <textarea
            data-testid="contact-message"
            value={form.message}
            onChange={update("message")}
            rows={5}
            placeholder="What's on your mind?"
            className="hearth-input mt-4 resize-none"
            required
          />
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="btn-hearth mt-6 inline-flex items-center gap-2 rounded-full bg-[#C25934] hover:bg-[#A04728] disabled:opacity-60 text-white px-7 py-3 font-semibold"
          >
            <PaperPlaneTilt size={18} weight="duotone" />
            {loading ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </section>
  );
}
